import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Plus, Trash2, Edit, Euro, Handshake, Clock, CheckCircle2, XCircle, RotateCcw, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { AttachDocumentButton } from '../components/AttachDocumentButton';

const API = ((typeof window !== "undefined" && window.__API_URL__) || process.env.REACT_APP_BACKEND_URL) + '/api';

const STATUS_CONFIG = {
  envoyee: { label: 'Envoyee', color: 'bg-blue-500/10 text-blue-400 border-blue-500/20', icon: Clock },
  en_negociation: { label: 'En negociation', color: 'bg-amber-500/10 text-amber-400 border-amber-500/20', icon: Handshake },
  acceptee: { label: 'Acceptee', color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20', icon: CheckCircle2 },
  refusee: { label: 'Refusee', color: 'bg-red-500/10 text-red-400 border-red-500/20', icon: XCircle },
  retiree: { label: 'Retiree', color: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20', icon: RotateCcw },
};

const OfferForm = ({ offer, properties, onSave, onClose }) => {
  const [form, setForm] = useState({
    property_id: offer?.property_id || '',
    prix_propose: offer?.prix_propose || '',
    contre_offre: offer?.contre_offre || '',
    status: offer?.status || 'envoyee',
    date_offre: offer?.date_offre || format(new Date(), 'yyyy-MM-dd'),
    date_reponse: offer?.date_reponse || '',
    notes: offer?.notes || '',
    conditions: offer?.conditions || '',
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = {
        ...form,
        prix_propose: parseFloat(form.prix_propose),
        contre_offre: form.contre_offre ? parseFloat(form.contre_offre) : null,
      };
      if (offer?.id) {
        await axios.put(`${API}/offers/${offer.id}`, data, { withCredentials: true });
        toast.success('Offre mise a jour');
      } else {
        await axios.post(`${API}/offers`, data, { withCredentials: true });
        toast.success('Offre creee');
      }
      onSave(); onClose();
    } catch { toast.error('Erreur'); } finally { setLoading(false); }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label className="text-zinc-300">Bien</Label>
        <Select value={form.property_id} onValueChange={(v) => setForm({ ...form, property_id: v })}>
          <SelectTrigger className="bg-zinc-950 border-zinc-800 text-white" data-testid="offer-property-select"><SelectValue placeholder="Selectionner" /></SelectTrigger>
          <SelectContent>{properties.map(p => <SelectItem key={p.id} value={p.id}>{p.title} - {p.city} ({p.price?.toLocaleString('fr-FR')} EUR)</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-zinc-300">Prix propose (EUR)</Label>
          <Input type="number" value={form.prix_propose} onChange={(e) => setForm({ ...form, prix_propose: e.target.value })} required className="bg-zinc-950 border-zinc-800 text-white" data-testid="offer-price-input" />
        </div>
        <div>
          <Label className="text-zinc-300">Contre-offre (EUR)</Label>
          <Input type="number" value={form.contre_offre} onChange={(e) => setForm({ ...form, contre_offre: e.target.value })} className="bg-zinc-950 border-zinc-800 text-white" />
        </div>
        <div>
          <Label className="text-zinc-300">Statut</Label>
          <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
            <SelectTrigger className="bg-zinc-950 border-zinc-800 text-white"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(STATUS_CONFIG).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-zinc-300">Date offre</Label>
          <Input type="date" value={form.date_offre} onChange={(e) => setForm({ ...form, date_offre: e.target.value })} className="bg-zinc-950 border-zinc-800 text-white" />
        </div>
      </div>
      <div>
        <Label className="text-zinc-300">Conditions</Label>
        <Textarea value={form.conditions} onChange={(e) => setForm({ ...form, conditions: e.target.value })} className="bg-zinc-950 border-zinc-800 text-white min-h-[60px]" placeholder="Conditions suspensives, delais..." />
      </div>
      <div>
        <Label className="text-zinc-300">Notes</Label>
        <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-zinc-950 border-zinc-800 text-white min-h-[60px]" />
      </div>
      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="outline" onClick={onClose} className="border-zinc-700 text-zinc-300">Annuler</Button>
        <Button type="submit" disabled={loading} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="save-offer-button">{offer?.id ? 'Modifier' : 'Creer'}</Button>
      </div>
    </form>
  );
};

export const OffersPage = () => {
  const navigate = useNavigate();
  const [offers, setOffers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingOffer, setEditingOffer] = useState(null);

  const fetchData = async () => {
    try {
      const [offRes, propRes, projRes] = await Promise.all([
        axios.get(`${API}/offers`, { withCredentials: true }),
        axios.get(`${API}/properties`, { withCredentials: true }),
        axios.get(`${API}/projects`, { withCredentials: true })
      ]);
      setOffers(offRes.data);
      setProperties(propRes.data);
      setProjects(projRes.data);
    } catch { /* ignore */ } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const getProjectForOffer = (offer) => projects.find(p => p.property_id === offer.property_id);

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/offers/${id}`, { withCredentials: true });
      toast.success('Offre supprimee');
      fetchData();
    } catch (err) {
      console.error('Delete offer error:', err);
      toast.error('Erreur lors de la suppression');
    }
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="offers-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white font-['Outfit'] tracking-tight">Offres d'achat</h1>
          <p className="text-zinc-400 mt-1">Suivez vos offres et negociations</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => { setLoading(true); fetchData(); }} className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-zinc-700 text-zinc-400 hover:text-white hover:border-zinc-500 text-sm transition-colors" data-testid="refresh-button">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Actualiser
          </button>
          <Button className="bg-white text-black hover:bg-zinc-200" onClick={() => { setEditingOffer(null); setDialogOpen(true); }} data-testid="add-offer-button">
            <Plus className="w-4 h-4 mr-2" /> Nouvelle offre
          </Button>
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg max-h-[90vh] overflow-y-auto w-[95vw]">
          <DialogHeader><DialogTitle className="text-white">{editingOffer ? 'Modifier l\'offre' : 'Nouvelle offre'}</DialogTitle></DialogHeader>
          <OfferForm offer={editingOffer} properties={properties} onSave={fetchData} onClose={() => { setDialogOpen(false); setEditingOffer(null); }} />
        </DialogContent>
      </Dialog>

      {offers.length > 0 ? (
        <div className="space-y-3">
          {offers.map(offer => {
            const config = STATUS_CONFIG[offer.status] || STATUS_CONFIG.envoyee;
            const Icon = config.icon;
            const reduction = offer.property_price ? ((1 - offer.prix_propose / offer.property_price) * 100) : 0;
            return (
              <Card key={offer.id} className="bg-zinc-900 border-zinc-800 card-hover">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 flex-1">
                      <div className="w-10 h-10 rounded-lg bg-zinc-800 flex items-center justify-center">
                        <Icon className="w-5 h-5 text-zinc-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          {offer.property_id ? (
                            <button
                              onClick={() => navigate(`/app/recherche?property_id=${offer.property_id}`)}
                              className="font-medium text-white hover:text-emerald-400 underline-offset-2 hover:underline transition-colors text-left"
                              data-testid={`offer-nav-property-${offer.id}`}
                              title="Voir la fiche du bien"
                            >
                              {offer.property_title || 'N/A'}
                            </button>
                          ) : (
                            <h3 className="font-medium text-white">{offer.property_title || 'N/A'}</h3>
                          )}
                          <Badge variant="outline" className={config.color}>{config.label}</Badge>
                        </div>
                        <p className="text-sm text-zinc-500">{offer.property_city} | Offre: {offer.date_offre}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-mono font-semibold text-white">{offer.prix_propose?.toLocaleString('fr-FR')} EUR</p>
                        {offer.property_price && (
                          <p className="text-xs text-zinc-500">
                            Prix: {offer.property_price.toLocaleString('fr-FR')} EUR
                            {reduction > 0 && <span className="text-amber-400 ml-1">(-{reduction.toFixed(1)}%)</span>}
                          </p>
                        )}
                        {offer.contre_offre && (
                          <p className="text-xs text-blue-400">Contre-offre: {offer.contre_offre.toLocaleString('fr-FR')} EUR</p>
                        )}
                      </div>
                      <div className="flex gap-1 ml-4">
                        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => { setEditingOffer(offer); setDialogOpen(true); }}>
                          <Edit className="w-4 h-4 text-zinc-400" />
                        </Button>
                        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => handleDelete(offer.id)}>
                          <Trash2 className="w-4 h-4 text-red-400" />
                        </Button>
                      </div>
                    </div>
                  </div>
                  {/* Attach compromis signé PDF when offer accepted */}
                  {offer.status === 'acceptee' && (() => {
                    const proj = getProjectForOffer(offer);
                    return (
                      <div className="mt-3 pt-3 border-t border-zinc-800 flex items-center gap-2 flex-wrap">
                        <span className="text-[11px] text-zinc-500 flex items-center gap-1">
                          <CheckCircle2 className="w-3 h-3 text-emerald-500" /> Offre acceptée —
                        </span>
                        <AttachDocumentButton
                          projectId={proj?.id}
                          folder="compromis"
                          label="Compromis signé"
                          fileId={proj?.compromis_file_id}
                          testId={`offer-attach-compromis-${offer.id}`}
                          onAttached={async (fileId) => {
                            if (!proj) return;
                            await axios.patch(`${API}/projects/${proj.id}/attach-document?kind=compromis&file_id=${fileId}`, {}, { withCredentials: true });
                            fetchData();
                          }}
                        />
                        {!proj && (
                          <span className="text-[10px] text-amber-400 italic">Aucun projet lié — créez-le depuis le Pipeline</span>
                        )}
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="py-16 text-center">
            <Handshake className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">Aucune offre</h3>
            <p className="text-zinc-500">Commencez par envoyer une offre sur un bien</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
